<script lang="ts" setup>
// -----------------------
// props & emits
// -----------------------
import * as Sentry from "@sentry/vue";
import {
  viewItemList as sendViewItemListEvent,
  viewSimilarItem as sendViewSimilarItemEvent,
  viewRelatedItem as sendViewRelatedItemEvent,
} from "~/composables/useAnalytics";

const props = withDefaults(
  defineProps<{
    filters?: string;
    showOutOfStock?: boolean;
    onlyQuickView?: boolean;
    title?: string;
    method?: "filter" | "similar";
    similarSourceId?: number;
    products?: any[];
    googleAnalyticsEvent?: string;
  }>(),
  {
    filters: "",
    showOutOfStock: false,
    onlyQuickView: false,
    title: "",
    method: "filter",
    similarSourceId: undefined,
    products: undefined,
    googleAnalyticsEvent: "",
  },
);

// -----------------------
// composables
// -----------------------
const config = useRuntimeConfig();
const themeGroup = config.public.theme.group;

// -----------------------
// reactive properties
// -----------------------
const baseWidth = ref(0);
const parentElement = ref(null);
const productCards = ref(null);
const carouselProducts = ref<any[]>([]);

// -----------------------
// computed properties
// -----------------------
const extendedFilters = computed(() => {
  const parts = [];

  if (!props.showOutOfStock) {
    parts.push("stock > 0");
  }

  if (props.filters && props.filters.length > 0) {
    parts.push(props.filters);
  }

  return parts.join(" AND ");
});

const visibleItems = computed(() => {
  if (baseWidth.value > 1440) {
    return 5;
  } else if (baseWidth.value > 1024) {
    return 4;
  } else if (baseWidth.value > 640) {
    return 3;
  } else {
    return 2;
  }
});

const parentStyle = computed(() => {
  if (
    !process.client ||
    !productCards.value ||
    !Array.isArray(productCards.value) ||
    productCards.value.length === 0
  ) {
    return {};
  }

  const style =
    productCards.value[0].$el.currentStyle ||
    window.getComputedStyle(productCards.value[0].$el);
  const marginX = parseFloat(style.marginLeft) + parseFloat(style.marginRight);
  const borderX =
    parseFloat(style.borderLeftWidth) + parseFloat(style.borderRightWidth);
  const totalWidth = productCards.value[0].$el.offsetWidth + marginX + borderX;
  const width = Math.min(64 + visibleItems.value * totalWidth, baseWidth.value);

  return {
    width: `${width}px`,
  };
});

// -----------------------
// helper methods
// -----------------------
const updateBaseWidth = () => {
  if (process.client && document?.body) {
    baseWidth.value = document.body.clientWidth;
  }
};

// -----------------------
// vue events
// -----------------------
watch(carouselProducts, () => {
  sendViewItemListEvent(carouselProducts.value, { listName: props.title });
});

// Watch for changes to products prop and update carouselProducts
watch(
  () => props.products,
  (newProducts) => {
    if (newProducts && newProducts.length > 0) {
      carouselProducts.value = newProducts;
    }
  },
  { deep: true },
);

onMounted(() => {
  updateBaseWidth();
  if (process.client) {
    window.addEventListener("resize", updateBaseWidth);
  }
});

onUpdated(() => {
  if (process.client) {
    updateBaseWidth();
  }
});

// -----------------------
// products load logic
// -----------------------
// If products are provided as props, use them directly (for SSR)
if (props.products && props.products.length > 0) {
  carouselProducts.value = props.products;
} else {
  // Otherwise fetch products via API (for client-side dynamic loading)
  try {
    const requestBody: any = {};

    if (props.method === "filter") {
      // Use filter-based search (existing behavior)
      requestBody.filter = extendedFilters.value;
    } else if (props.method === "similar" && props.similarSourceId) {
      // Use vector-based similar products search
      requestBody.similarTo = props.similarSourceId;
      requestBody.embedder = "default";
      requestBody.limit = 10; // Limit similar products
    }

    let response;
    if (props.method === "similar") {
      // Use the dedicated similar products endpoint
      response = await $fetch(`/api/meilisearch/similar-products`, {
        method: "POST",
        body: {
          id: requestBody.similarTo,
          embedder: requestBody.embedder,
          limit: requestBody.limit,
        },
      });
    } else {
      // Use the regular masterproducts endpoint for filter-based search
      response = await $fetch(`/api/meilisearch/masterproducts`, {
        method: "POST",
        body: requestBody,
      });
    }

    // Handle different response types and ensure we have an array
    if (Array.isArray(response)) {
      carouselProducts.value = response;
    } else if (response && typeof response === "object" && "hits" in response) {
      // Handle search response format with hits array
      carouselProducts.value = response.hits || [];
    } else {
      carouselProducts.value = [];
    }
  } catch (error) {
    Sentry.captureEvent({
      message: `An error occured when fetching master products for product carousel`,
      level: "error",
      extra: {
        method: props.method,
        filter: props.method === "filter" ? extendedFilters.value : null,
        similarSourceId:
          props.method === "similar" ? props.similarSourceId : null,
        error,
      },
    });
  }
}

const sendEventIfNeeded = (product: any) => {
  // console.log("send event if needed: ", props.googleAnalyticsEvent);
  // console.log(product);

  if (props.googleAnalyticsEvent === "viewSimilarItem") {
    sendViewSimilarItemEvent(product);
  } else if (props.googleAnalyticsEvent === "viewRelatedItem") {
    sendViewRelatedItemEvent(product);
  }
};
</script>

<template>
  <div
    v-if="carouselProducts"
    class="flex justify-center"
    :class="{ 'bg-brand-secondary': themeGroup === 'mountex' }"
  >
    <CarouselBase
      v-if="carouselProducts.length > 0"
      ref="parentElement"
      :title="title"
      :total-items="carouselProducts.length"
      :scroll-arrow-size="25"
      :scroll-arrow-spacing="7"
      :infinite="true"
      class="py-[30px]"
      :style="parentStyle"
      :visible-items="visibleItems"
    >
      <ProductListProductCard
        v-for="product in carouselProducts"
        ref="productCards"
        :key="product.sku"
        :product="product"
        :only-quick-view="onlyQuickView"
        :disable-variant-change-on-swipe="true"
        palette=""
        class="grow-0 shrink-0 basis-[170px] md:basis-[218px] xl:basis-[250px] h-auto ml-[6px] mr-[7px] md:ml-[13px] md:mr-[13px] 2xl:ml-[12px] 2xl:mr-[12px] mb-6 shadow-xl"
        @click="sendEventIfNeeded(product)"
      >
        <template #name="{ url }">
          <a v-if="!onlyQuickView" :href="url" class="text-black unstyled">
            <div
              v-if="themeGroup === 'mountex'"
              class="font-bold font-lora text-2.5 md:text-xs xl:text-sm"
            >
              {{ product.masterproduct_gyarto_property }}
            </div>
            <div class="text-2.5 md:text-xs xl:text-sm">{{ product.name }}</div>
          </a>
          <div v-else class="text-black unstyled">
            <div
              v-if="themeGroup === 'mountex'"
              class="font-bold font-lora text-2.5 md:text-xs xl:text-sm"
            >
              {{ product.masterproduct_gyarto_property }}
            </div>
            <div class="text-2.5 md:text-xs xl:text-sm">{{ product.name }}</div>
          </div>
        </template>
      </ProductListProductCard>
    </CarouselBase>
  </div>
</template>
